import type { TranslateService } from '@ngx-translate/core';
import { parseProblemFromError, type ParsedProblem } from './extract-problem-message';

/**
 * Resolves a server-side `ProblemException` into a translated, user-facing inline error message.
 *
 * Order of preference:
 *  1. Type-specific i18n key derived from `<type>` (and optional `data.reason`/`data.cause`)
 *  2. The supplied `fallbackKey` — a generic translated message for the calling context
 *
 * The server's `title`/`detail` strings are intentionally **not** surfaced to users — those are
 * English API-debug text, not user-facing copy. Frontend switches on `type` + `data` to render
 * its own localized messages. New problem types fall through to the fallback without breaking;
 * adding a friendlier message is a one-line edit in `PROBLEM_KEYS`.
 */
export async function translateProblem(
  err: unknown,
  translate: TranslateService,
  fallbackKey: string,
): Promise<string> {
  const problem = await parseProblemFromError(err);
  if (!problem) {
    return translate.instant(fallbackKey);
  }

  const key = lookupKey(problem);
  if (key) {
    return translate.instant(key, problemInterpolation(problem));
  }
  return translate.instant(fallbackKey);
}

/**
 * Strips the `https://problems.tourfold.com/` prefix the backend builds via
 * `ProblemDetailConfig.buildTypeUri`. The lookup table on this side keeps the namespace+slug pair
 * (e.g. `document-management/name-taken`) so it stays readable; the URL prefix is implementation
 * detail of the wire format, not of the problem identity.
 */
const PROBLEMS_BASE_URI = 'https://problems.tourfold.com/';

function normalizeType(type: string | undefined): string | undefined {
  if (!type) return undefined;
  return type.startsWith(PROBLEMS_BASE_URI) ? type.slice(PROBLEMS_BASE_URI.length) : type;
}

/** Picks the most specific i18n key the problem maps to, or null when we don't yet localize it. */
function lookupKey(problem: ParsedProblem): string | null {
  const type = normalizeType(problem.type);
  const reason = stringValue(problem.data['reason']);
  const cause = stringValue(problem.data['cause']);
  if (!type) return null;

  // Composite lookup first (type#reason or type#cause), then plain type.
  if (reason) {
    const composite = PROBLEM_KEYS[`${type}#${reason}`];
    if (composite) return composite;
  }
  if (cause) {
    const composite = PROBLEM_KEYS[`${type}#${cause}`];
    if (composite) return composite;
  }
  return PROBLEM_KEYS[type] ?? null;
}

/** Pulls discriminator fields out of `data` so translations can interpolate them. */
function problemInterpolation(problem: ParsedProblem): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of ['name', 'fileName', 'parentId', 'kind', 'cause', 'reason', 'expected', 'actual'] as const) {
    const v = problem.data[key];
    if (typeof v === 'string') out[key] = v;
  }
  return out;
}

function stringValue(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

/**
 * Mapping from `<type>` (and optional `#<reason|cause>` discriminator) to translation key.
 * Keep this list in sync with `DocumentManagementProblem` on the backend.
 */
const PROBLEM_KEYS: Readonly<Record<string, string>> = {
  // ── Folder + document name validation ─────────────────────────────────
  'document-management/name-taken': 'DOCUMENT_MANAGEMENT.PROBLEM.NAME_TAKEN',
  'document-management/name-taken#reserved_managed_namespace':
    'DOCUMENT_MANAGEMENT.PROBLEM.NAME_TAKEN_RESERVED_NAMESPACE',
  'document-management/invalid-name#length_exceeded':
    'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_NAME_LENGTH',
  'document-management/invalid-name#forbidden_codepoint':
    'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_NAME_CHARS',
  'document-management/invalid-name#reserved_name':
    'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_NAME_RESERVED',
  'document-management/invalid-name#empty_after_trim':
    'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_NAME_EMPTY',

  // ── Folder lifecycle ──────────────────────────────────────────────────
  'document-management/parent-not-found': 'DOCUMENT_MANAGEMENT.PROBLEM.PARENT_NOT_FOUND',
  'document-management/folder-not-found': 'DOCUMENT_MANAGEMENT.PROBLEM.FOLDER_NOT_FOUND',
  'document-management/cannot-modify-managed-folder':
    'DOCUMENT_MANAGEMENT.PROBLEM.CANNOT_MODIFY_MANAGED_FOLDER',
  'document-management/cannot-modify-managed-folder#cannot_create_under_managed_branch':
    'DOCUMENT_MANAGEMENT.PROBLEM.CREATE_UNDER_MANAGED_BRANCH',
  'document-management/cannot-modify-soft-deleted-document':
    'DOCUMENT_MANAGEMENT.PROBLEM.CANNOT_MODIFY_SOFT_DELETED_DOCUMENT',
  'document-management/move-into-descendant':
    'DOCUMENT_MANAGEMENT.PROBLEM.MOVE_INTO_DESCENDANT',
  'document-management/invalid-move-target':
    'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_MOVE_TARGET',
  'document-management/invalid-retention-days':
    'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_RETENTION_DAYS',
  'document-management/move-across-managed-boundary':
    'DOCUMENT_MANAGEMENT.PROBLEM.MOVE_ACROSS_MANAGED_BOUNDARY',
  'document-management/must-be-soft-deleted-first':
    'DOCUMENT_MANAGEMENT.PROBLEM.MUST_BE_SOFT_DELETED_FIRST',

  // ── Document lifecycle ────────────────────────────────────────────────
  'document-management/document-not-found': 'DOCUMENT_MANAGEMENT.PROBLEM.DOCUMENT_NOT_FOUND',
  'document-management/version-not-found': 'DOCUMENT_MANAGEMENT.PROBLEM.VERSION_NOT_FOUND',
  'document-management/version-not-found#pending_upload':
    'DOCUMENT_MANAGEMENT.PROBLEM.VERSION_PENDING',
  'document-management/version-not-found#wrong_document':
    'DOCUMENT_MANAGEMENT.PROBLEM.VERSION_WRONG_DOCUMENT',
  'document-management/version-mime-mismatch':
    'DOCUMENT_MANAGEMENT.PROBLEM.VERSION_MIME_MISMATCH',

  // ── Upload pipeline (cause-discriminated) ─────────────────────────────
  // The backend sets `data.cause` to the underlying `filestore/*` slug; we dispatch on
  // `<wrapper-slug>#<cause-slug>` to surface a specific message for each failure mode.
  // Unrecognised causes fall back to the wrapper-only entry above.
  'document-management/upload-initiate-failed':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_INITIATE_FAILED',
  'document-management/upload-initiate-failed#filestore/file-not-found':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_FILE_NOT_FOUND',
  'document-management/upload-initiate-failed#filestore/upload-content-empty':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_CONTENT_EMPTY',
  'document-management/upload-initiate-failed#filestore/upload-mime-type-blank':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_MIME_TYPE_MISSING',
  'document-management/upload-initiate-failed#filestore/upload-original-file-name-blank':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_FILE_NAME_MISSING',
  'document-management/upload-initiate-failed#filestore/upload-rejected':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_REJECTED',
  'document-management/upload-initiate-failed#filestore/rendition-source-not-supported':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_RENDITION_SOURCE_NOT_SUPPORTED',
  'document-management/upload-initiate-failed#filestore/rendition-target-format-not-supported':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_RENDITION_TARGET_FORMAT_NOT_SUPPORTED',
  'document-management/upload-initiate-failed#filestore/rendition-source-decode-failed':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_RENDITION_DECODE_FAILED',
  'document-management/upload-initiate-failed#filestore/rendition-pdf-empty':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_RENDITION_PDF_EMPTY',
  'document-management/upload-finalize-failed':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_FINALIZE_FAILED',
  'document-management/upload-finalize-failed#filestore/file-not-found':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_FILE_NOT_FOUND',
  'document-management/upload-finalize-failed#filestore/upload-rejected':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_REJECTED',
  'document-management/upload-finalize-failed#filestore/rendition-source-decode-failed':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_RENDITION_DECODE_FAILED',
  'document-management/upload-finalize-failed#filestore/rendition-pdf-empty':
    'DOCUMENT_MANAGEMENT.PROBLEM.UPLOAD_RENDITION_PDF_EMPTY',
};
